import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para operações de grupos (logs condicionais por ambiente)
const groupLogger = createLogger('GroupUtils');

/**
 * Hook para utilitários de grupos WhatsApp
 * 
 * Abstrai chamadas à provider-proxy para operações de grupos,
 * mantendo arquitetura multi-provider.
 * 
 * @returns Funções para sincronizar e buscar dados de grupos
 */
export function useGroupUtils() {
  /**
   * Sincroniza todos os grupos do WhatsApp da inbox
   * 
   * @param inboxId - ID da inbox
   * @returns Lista de grupos sincronizados
   */
  const syncGroups = useCallback(async (inboxId: string) => {
    groupLogger.debug('Sincronizando grupos', { inboxId });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: { 
        inbox_id: inboxId, 
        action: 'sync_groups' 
      }
    });
    
    if (error) {
      groupLogger.error('Erro ao sincronizar grupos', error);
      throw error;
    }
    
    groupLogger.info('Grupos sincronizados', { count: data?.data?.length || 0 });
    return data?.data || [];
  }, []);

  /**
   * Busca metadados de um grupo específico
   * 
   * @param inboxId - ID da inbox
   * @param groupJid - JID do grupo (ex: "120363@g.us")
   * @returns Metadados do grupo
   */
  const getGroupMetadata = useCallback(async (inboxId: string, groupJid: string) => {
    groupLogger.debug('Buscando metadados do grupo', { inboxId, groupJid });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: { 
        inbox_id: inboxId, 
        action: 'get_group_metadata',
        group_jid: groupJid
      }
    });
    
    if (error) {
      groupLogger.error('Erro ao buscar metadados', error);
      throw error;
    }
    
    return data?.data || null;
  }, []);

  /**
   * Busca participantes de um grupo
   * 
   * @param inboxId - ID da inbox
   * @param groupJid - JID do grupo (ex: "120363@g.us")
   * @returns Lista de participantes do grupo
   */
  const getGroupParticipants = useCallback(async (inboxId: string, groupJid: string) => {
    groupLogger.debug('Buscando participantes do grupo', { inboxId, groupJid });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: { 
        inbox_id: inboxId, 
        action: 'get_group_participants',
        group_jid: groupJid
      }
    });
    
    if (error) {
      groupLogger.error('Erro ao buscar participantes', error);
      throw error;
    }
    
    groupLogger.info('Participantes obtidos', { count: data?.data?.length || 0 });
    return data?.data || [];
  }, []);

  /**
   * Altera a foto de perfil do grupo
   * 
   * @param inboxId - ID da inbox
   * @param groupJid - JID do grupo (ex: "120363@g.us")
   * @param pictureData - URL da imagem ou string base64
   * @returns Resultado da operação
   */
  const setGroupPicture = useCallback(async (inboxId: string, groupJid: string, pictureData: string) => {
    groupLogger.debug('Alterando foto do grupo', { inboxId, groupJid });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: { 
        inbox_id: inboxId, 
        action: 'set_group_picture',
        group_jid: groupJid,
        picture_data: pictureData
      }
    });
    
    if (error) {
      groupLogger.error('Erro ao alterar foto do grupo', error);
      throw error;
    }
    
    groupLogger.info('Foto do grupo alterada com sucesso');
    return data?.data || { updated: true };
  }, []);

  return {
    syncGroups,
    getGroupMetadata,
    getGroupParticipants,
    setGroupPicture,
  };
}